import React, { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DriverBulkUploadModal from "../components/DriverBulkUploadModal";
import SimpleDriverBulkUploadModal from "../components/SimpleDriverBulkUploadModal";
import DriverStatusSummaryCards from "../components/DriverStatusSummaryCards";
import DriverContactCallStatusModal from "../components/DriverContactCallStatusModal";
import ActivityLogsModal from "../components/ActivityLogsModal";
import AssignedEmployeeStatsPanel from "../components/AssignedEmployeeStatsPanel";
import SortableTh from "../components/SortableTh";
import DriverAnalyticsDateFilters from "../components/DriverAnalyticsDateFilters";
import DriverCompareFilters from "../components/DriverCompareFilters";
import {
  computeComparisonRange,
  formatBreakdownDateRangeLabel,
} from "../utils/rideBreakdownComparison";
import { useProfile } from "../context/ProfileContext";
import { STATE_UT_OPTIONS, getStateUtDisplayLabel } from "../constants/stateUtOptions";
import {
  FaSpinner,
  FaCar,
  FaUserTie,
  FaPhone,
  FaIdCard,
  FaEye,
  FaEdit,
  FaCalendar,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaSearch,
  FaFilter,
  FaUpload,
  FaDownload,
  FaPlus,
  FaClock,
  FaGlobe,
  FaTag,
  FaColumns,
  FaTrash,
  FaHistory,
} from "react-icons/fa";

const STATUS_OPTIONS = ["Pending", "In Revisit", "Verified", "Rejected", "Onboarded"];

// Call Status options – must match Driver model callStatusHistory.status enum
const CALL_STATUS_OPTIONS = [
  "Not Answered",
  "Number Not Valid",
  "Agreed to Fill the Form",
  "Agreed to Send Documents",
  "Not Interested",
  "Message Sent",
  "Docs Received",
  "Docs Uploaded",
  "Custom Filed",
];

// Call Type (Contact & Call Status) – must match Driver model callStatusHistory.type enum
const CALL_TYPE_OPTIONS = ["Initial Call", "1st Followup", "2nd Followup", "3rd Followup"];

const CONTACT_SOURCE_OPTIONS = [
  { value: "", label: "Search not listed" },
  { value: "WhatsApp Group", label: "WhatsApp Group" },
  { value: "Google Ads", label: "Google Ads" },
  { value: "Meta Ads", label: "Meta Ads" },
  { value: "Google Search Organic", label: "Google Search Organic" },
  { value: "Job Posting Paid", label: "Job Posting Paid" },
  { value: "Job Posting Organic", label: "Job Posting Organic" },
  { value: "Apna Jobs Paid", label: "Apna Jobs Paid" },
  { value: "Workindia Paid", label: "Workindia Paid" },
  { value: "OLX Paid", label: "OLX Paid" },
  { value: "Apna Jobs Organic", label: "Apna Jobs Organic" },
  { value: "Workindia Organic", label: "Workindia Organic" },
  { value: "OLX Organic", label: "OLX Organic" },
  { value: "Custom", label: "Custom" },
];

const toLocalDateString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fromLocalDateString = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const normalizeDriverStatusKey = (value) => {
  if (!value || !String(value).trim()) return "";
  return value === "In Revisit" ? "Revisit" : String(value).trim();
};

const DRIVERS_LIST_REQUEST_TIMEOUT_MS = 120000;

const STATUS_CARD_KEYS = ["Pending", "Revisit", "Verified", "Rejected", "Onboarded"];

const getRejectedOrRevisitReasons = (driver) => {
  const sections = [
    { type: "DL", data: driver.dl },
    { type: "Aadhaar", data: driver.aadhaar },
    { type: "PAN", data: driver.pan },
  ];
  return sections
    .filter(
      (s) =>
        s.data &&
        (s.data.status === "Rejected" || s.data.status === "Revisit")
    )
    .map((s) => ({
      type: s.type,
      statusReason: s.data.statusReason?.trim() || "",
    }));
};

const Drivers = () => {
  const { profile, assignModule } = useProfile();
  const isNewType = profile?.employeeType === "new";
  const driverModule = isNewType && assignModule?.driver?.enabled ? assignModule.driver : null;
  // Edit option only for SuperAdmin (role name "SuperAdmin" or slug "superadmin")
  const isSuperAdmin =
    profile?.role?.name === "SuperAdmin" ||
    profile?.role?.slug === "superadmin" ||
    profile?.email === "superadmin@udrivo.in";
  const hasDriverFullAccess = isSuperAdmin || !!driverModule?.fullAccess;
  const canShowQuickUpload = isSuperAdmin || !!driverModule?.quickUploadAccess;
  const canShowAddDriver = isSuperAdmin || !!driverModule?.addDriverAccess;
  const hasNoAllowedDriverStatuses =
    isNewType && driverModule && !(driverModule.allowedStatuses?.length);
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dlIssueDate, setDlIssueDate] = useState(null);
  const [dlExpiryDate, setDlExpiryDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showSimpleBulkUploadModal, setShowSimpleBulkUploadModal] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [showAssignEmployeeModal, setShowAssignEmployeeModal] = useState(false);
  const [assignModalEmployeeId, setAssignModalEmployeeId] = useState("");
  const [assignModalAssignedDate, setAssignModalAssignedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newEmployeesList, setNewEmployeesList] = useState([]);
  const [assignedEmployeeFilterOptions, setAssignedEmployeeFilterOptions] = useState([]);
  const [assignModalLoading, setAssignModalLoading] = useState(false);
  const [assignSubmitLoading, setAssignSubmitLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    contactSource: true,
    assignee: true,
    assignedEmployee: true,
    assignedBy: true,
    createdBy: true,
    reporter: true,
    language: true,
    callStatus: true,
    documents: true,
    dlIssueDate: true,
    dlExpiryDate: true,
    dlLastUpdatedAt: true,
    ridesCompleted: true,
    driverRating: true,
    locationStatus: true,
    currentLocation: true,
    totalEarnings: true,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filters, setFilters] = useState({
    contactSource: "",
    stateUt: "",
    location: "",
    area: "",
    status: "",
    assignee: "",
    assignedEmployeeId: "",
    reporter: "",
    language: "",
    callStatus: "",
    callType: "",
    callTypeAny: "",
    initialCallStatus: "",
    panelStatus: "",
    hasCompletedRide: "",
    locationNotAvailable: "",
    dlImage: "",
    aadhaarImage: "",
    panImage: "",
    dlNumber: "",
    aadhaarNumber: "",
    panNumber: "",
  });
  const [contactSourceDropdownOpen, setContactSourceDropdownOpen] = useState(false);
  const [contactSourceSearch, setContactSourceSearch] = useState("");
  const contactSourceDropdownRef = useRef(null);
  const [stateUtDropdownOpen, setStateUtDropdownOpen] = useState(false);
  const [stateUtSearch, setStateUtSearch] = useState("");
  const stateUtDropdownRef = useRef(null);
  const [contactCallStatusModalDriverId, setContactCallStatusModalDriverId] = useState(null);
  const [activityLogsModal, setActivityLogsModal] = useState({
    show: false,
    module: "Driver",
    entityId: null,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDrivers: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20,
  });
  // Driver Details Breakdown cards (default all, then filter-aware)
  const [driverBreakdownData, setDriverBreakdownData] = useState({
    Registered: 0,
    Pending: 0,
    Revisit: 0,
    Verified: 0,
    Rejected: 0,
    Onboarded: 0,
    JoiningFeeCount: 0,
    RideCompleted: 0,
    LocationNotAvailable: 0,
    joiningFeePaid: 0,
    joiningFeeDue: 0,
    joiningFeeDueCount: 0,
    joiningFee: null,
  });
  const [driverBreakdownLoading, setDriverBreakdownLoading] = useState(false);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [hideAssignedToEmployee, setHideAssignedToEmployee] = useState(false);
  const [compareMode, setCompareMode] = useState("previous_period");
  const [compareStartDate, setCompareStartDate] = useState("");
  const [compareEndDate, setCompareEndDate] = useState("");
  const [appliedComparison, setAppliedComparison] = useState(null);
  const [driverComparisonBreakdown, setDriverComparisonBreakdown] = useState(null);
  const [comparisonBreakdownLoading, setComparisonBreakdownLoading] = useState(false);
  const [dateFilterApplied, setDateFilterApplied] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusFilter = queryParams.get("status"); // e.g. 'Pending', 'Onboarded'

  // Refs to hold latest list-fetch params so callbacks (e.g. Contact Source modal onSaved) always refetch with current filters
  const listParamsRef = useRef({
    startDate: null,
    endDate: null,
    dlIssueDate: null,
    dlExpiryDate: null,
    filters: {},
    phoneSearch: "",
    statusFilter: null,
    pagination: { currentPage: 1, limit: 20 },
    sortConfig: { key: null, direction: "asc" },
    hideAssignedToEmployee: false,
  });
  const breakdownRequestIdRef = useRef(0);
  const comparisonRequestIdRef = useRef(0);
  const appliedComparisonRef = useRef(null);
  const lastBreakdownContextRef = useRef({
    params: null,
    effectiveFilters: null,
    listFilteredTotal: 0,
  });
  useEffect(() => {
    appliedComparisonRef.current = appliedComparison;
  }, [appliedComparison]);

  useEffect(() => {
    listParamsRef.current = {
      ...listParamsRef.current,
      startDate,
      endDate,
      dlIssueDate,
      dlExpiryDate,
      filters,
      phoneSearch,
      statusFilter,
      pagination,
      hideAssignedToEmployee,
    };
  }, [startDate, endDate, dlIssueDate, dlExpiryDate, filters, phoneSearch, statusFilter, pagination, hideAssignedToEmployee]);

  const tableScrollContainerRef = useRef(null);
  const scrollPositionToRestoreRef = useRef(null);

  useEffect(() => {
    if (!loading && scrollPositionToRestoreRef.current !== null && tableScrollContainerRef.current) {
      const pos = scrollPositionToRestoreRef.current;
      scrollPositionToRestoreRef.current = null;
      requestAnimationFrame(() => {
        if (tableScrollContainerRef.current) tableScrollContainerRef.current.scrollTop = pos;
      });
    }
  }, [loading, drivers]);

  const getTotalFromListResponse = (responseData) => {
    const pag = responseData?.pagination || responseData?.data?.pagination;
    if (!pag) return Array.isArray(responseData?.drivers) ? responseData.drivers.length : 0;
    return Number(pag.totalDrivers ?? pag.total ?? 0);
  };

  const buildDriversListQuery = ({
    page,
    limit,
    params,
    effectiveFilters,
    forcedStatus,
    forBreakdownSummary = false,
  }) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (params.startDate) {
      queryParams.append("startDate", toLocalDateString(params.startDate));
    }
    if (params.endDate) {
      queryParams.append("endDate", toLocalDateString(params.endDate));
    }

    let statusVal = "";
    if (forBreakdownSummary || forcedStatus === null) {
      statusVal = "";
    } else if (typeof forcedStatus === "string") {
      statusVal = forcedStatus;
    } else {
      // Card/panel-driven status takes precedence; explicit dropdown status beats URL param.
      statusVal =
        effectiveFilters.panelStatus ||
        effectiveFilters.status ||
        params.statusFilter;
    }
    if (statusVal) {
      queryParams.append("status", statusVal);
    }

    if (params.phoneSearch && params.phoneSearch.trim()) {
      queryParams.append("search", params.phoneSearch.trim());
    }

    if (effectiveFilters.contactSource) queryParams.append("contactSource", effectiveFilters.contactSource);
    if (effectiveFilters.stateUt) queryParams.append("stateUt", effectiveFilters.stateUt);
    if (effectiveFilters.location) queryParams.append("location", effectiveFilters.location);
    if (effectiveFilters.area) queryParams.append("area", effectiveFilters.area);
    if (effectiveFilters.assignee) queryParams.append("assignee", effectiveFilters.assignee);
    if (
      effectiveFilters.assignedEmployeeId &&
      !( !forBreakdownSummary && params.hideAssignedToEmployee )
    ) {
      queryParams.append("assignedEmployeeId", effectiveFilters.assignedEmployeeId);
    }
    if (effectiveFilters.reporter) queryParams.append("reporter", effectiveFilters.reporter);
    if (effectiveFilters.language) queryParams.append("language", effectiveFilters.language);
    if (effectiveFilters.callStatus) queryParams.append("callStatus", effectiveFilters.callStatus);
    if (effectiveFilters.callType) queryParams.append("callType", effectiveFilters.callType);
    if (effectiveFilters.callTypeAny) queryParams.append("callTypeAny", effectiveFilters.callTypeAny);
    if (effectiveFilters.initialCallStatus) queryParams.append("initialCallStatus", effectiveFilters.initialCallStatus);
    if (effectiveFilters.hasCompletedRide) queryParams.append("hasCompletedRide", "1");
    if (effectiveFilters.locationNotAvailable) {
      queryParams.append("locationNotAvailable", "1");
    }
    if (effectiveFilters.dlImage) queryParams.append("dlImage", effectiveFilters.dlImage);
    if (effectiveFilters.aadhaarImage) queryParams.append("aadhaarImage", effectiveFilters.aadhaarImage);
    if (effectiveFilters.panImage) queryParams.append("panImage", effectiveFilters.panImage);
    if (effectiveFilters.dlNumber?.trim()) queryParams.append("dlNumber", effectiveFilters.dlNumber.trim());
    if (effectiveFilters.aadhaarNumber?.trim()) queryParams.append("aadhaarNumber", effectiveFilters.aadhaarNumber.trim());
    if (effectiveFilters.panNumber?.trim()) queryParams.append("panNumber", effectiveFilters.panNumber.trim());
    if (params.dlIssueDate) queryParams.append("dlIssueDate", toLocalDateString(params.dlIssueDate));
    if (params.dlExpiryDate) queryParams.append("dlExpiryDate", toLocalDateString(params.dlExpiryDate));

    if (!forBreakdownSummary && params.hideAssignedToEmployee) {
      queryParams.append("hideAssignedToEmployee", "1");
    }

    if (params.sortConfig?.key) {
      queryParams.append("sortBy", params.sortConfig.key);
      queryParams.append("sortOrder", params.sortConfig.direction || "desc");
    }

    return queryParams;
  };

  const buildBreakdownFromResponse = ({
    responseData,
    effectiveFilters,
    params,
    listFilteredTotal,
  }) => {
    const summary = responseData?.data?.statusBreakdown || {};
    const registered = Number(
      responseData?.data?.totalDrivers ?? summary.Registered ?? 0
    );
    const pending = Number(summary.Pending || 0);
    const revisit = Number(summary.Revisit || 0);
    const verified = Number(summary.Verified || 0);
    const rejected = Number(summary.Rejected || 0);
    const onboarded = Number(summary.Onboarded || 0);
    const joiningFeeCount = Number(summary.JoiningFeeCount || 0);
    const rideCompleted = Number(summary.RideCompleted || 0);
    const locationNotAvailable = Number(summary.LocationNotAvailable || 0);
    const joiningFeePaid = Number(responseData?.data?.joiningFeePaid || 0);
    const joiningFeeDue = Number(responseData?.data?.joiningFeeDue || 0);
    const joiningFeeDueCount = Number(responseData?.data?.joiningFeeDueCount || 0);

    let nextBreakdown = {
      Registered: registered,
      Pending: pending,
      Revisit: revisit,
      Verified: verified,
      Rejected: rejected,
      Onboarded: onboarded,
      JoiningFeeCount: joiningFeeCount,
      RideCompleted: rideCompleted,
      LocationNotAvailable: locationNotAvailable,
      joiningFeePaid,
      joiningFeeDue,
      joiningFeeDueCount,
      joiningFee: responseData?.data?.joiningFee || null,
    };

    const dropdownStatusKey =
      normalizeDriverStatusKey(effectiveFilters.status) ||
      normalizeDriverStatusKey(params.statusFilter);

    if (dropdownStatusKey && STATUS_CARD_KEYS.includes(dropdownStatusKey)) {
      const zeroed = Object.fromEntries(STATUS_CARD_KEYS.map((k) => [k, 0]));
      nextBreakdown = {
        Registered: registered,
        ...zeroed,
        [dropdownStatusKey]: Number(listFilteredTotal ?? 0),
        joiningFeePaid,
        joiningFeeDue,
        joiningFeeDueCount,
        joiningFee: responseData?.data?.joiningFee || null,
      };
    }

    return nextBreakdown;
  };

  const fetchBreakdownForDates = async ({
    params,
    effectiveFilters,
    listFilteredTotal,
    dateOverride = null,
  }) => {
    const filtersForBreakdown = {
      ...effectiveFilters,
      callTypeAny: "",
      initialCallStatus: "",
      panelStatus: "",
      hasCompletedRide: "",
      locationNotAvailable: "",
      status: "",
    };

    const requestParams = dateOverride
      ? {
          ...params,
          startDate: dateOverride.startDate,
          endDate: dateOverride.endDate,
          statusFilter: null,
        }
      : { ...params, statusFilter: null };

    const token = localStorage.getItem("adminToken");
    const query = buildDriversListQuery({
      page: 1,
      limit: 1,
      params: requestParams,
      effectiveFilters: filtersForBreakdown,
      forBreakdownSummary: true,
    });
    query.append("summaryMode", "true");

    const response = await axiosInstance.get(
      `${API.ADMIN.DRIVERS_LIST}?${query}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: DRIVERS_LIST_REQUEST_TIMEOUT_MS,
      }
    );

    return buildBreakdownFromResponse({
      responseData: response.data,
      effectiveFilters,
      params: requestParams,
      listFilteredTotal,
    });
  };

  const fetchDriverBreakdown = async ({ params, effectiveFilters, listFilteredTotal }) => {
    lastBreakdownContextRef.current = { params, effectiveFilters, listFilteredTotal };

    const requestId = breakdownRequestIdRef.current + 1;
    breakdownRequestIdRef.current = requestId;
    setDriverBreakdownLoading(true);

    try {
      const nextBreakdown = await fetchBreakdownForDates({
        params,
        effectiveFilters,
        listFilteredTotal,
      });

      if (breakdownRequestIdRef.current !== requestId) return;
      setDriverBreakdownData(nextBreakdown);

      if (appliedComparisonRef.current) {
        void fetchComparisonBreakdown(appliedComparisonRef.current);
      }
    } catch (err) {
      console.error("Error fetching driver breakdown:", err);
      if (breakdownRequestIdRef.current === requestId) {
        toast.error(
          err?.response?.status === 504
            ? "Driver breakdown timed out. Please try again or narrow filters."
            : "Failed to load Driver Details Breakdown."
        );
      }
    } finally {
      if (breakdownRequestIdRef.current === requestId) {
        setDriverBreakdownLoading(false);
      }
    }
  };

  const fetchComparisonBreakdown = async (comparison) => {
    if (!comparison?.startDate || !comparison?.endDate) {
      setDriverComparisonBreakdown(null);
      return;
    }

    const { params, effectiveFilters, listFilteredTotal } =
      lastBreakdownContextRef.current;

    if (!params) return;

    const requestId = comparisonRequestIdRef.current + 1;
    comparisonRequestIdRef.current = requestId;
    setComparisonBreakdownLoading(true);

    try {
      const nextBreakdown = await fetchBreakdownForDates({
        params,
        effectiveFilters,
        listFilteredTotal,
        dateOverride: {
          startDate: fromLocalDateString(comparison.startDate),
          endDate: fromLocalDateString(comparison.endDate),
        },
      });

      if (comparisonRequestIdRef.current !== requestId) return;
      setDriverComparisonBreakdown(nextBreakdown);
    } catch (err) {
      console.error("Error fetching driver comparison breakdown:", err);
      if (comparisonRequestIdRef.current === requestId) {
        setDriverComparisonBreakdown(null);
      }
    } finally {
      if (comparisonRequestIdRef.current === requestId) {
        setComparisonBreakdownLoading(false);
      }
    }
  };

  // Fetch drivers with pagination and filters.
  // overrideFilters: optional object merged over listParamsRef.current.filters so callers (e.g. card clicks)
  // can apply filter updates without waiting for the listParamsRef useEffect to commit.
  const fetchDrivers = async (page = 1, limit = null, overrideFilters = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = listParamsRef.current;
      const currentLimit = limit != null ? limit : params.pagination.limit;
      const effectiveFilters = overrideFilters
        ? { ...params.filters, ...overrideFilters }
        : params.filters;
      const queryParams = buildDriversListQuery({
        page,
        limit: currentLimit,
        params: {
          ...params,
          hideAssignedToEmployee: hasDriverFullAccess && params.hideAssignedToEmployee,
        },
        effectiveFilters,
      });

      const response = await axiosInstance.get(
        `${API.ADMIN.DRIVERS_LIST}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: DRIVERS_LIST_REQUEST_TIMEOUT_MS,
        }
      );

      console.log("Drivers API response:", response.data);

      // Handle different response structures
      const driversData = response.data.drivers || response.data.data?.drivers || response.data.data || [];

      const formattedDrivers = driversData.map((driver) => ({
        ...driver,
        createdAt: formatDateTime(driver.createdAt),
      }));

      setDrivers(formattedDrivers);
      setFilteredDrivers(formattedDrivers);

      // Update pagination from API response; always derive totalPages from totalDrivers and limit
      const pag = response.data.pagination || response.data.data?.pagination;
      const limitToUse = pag?.limit ?? currentLimit;
      const totalDriversFromApi = pag ? (pag.totalDrivers ?? pag.total ?? 0) : driversData.length;
      const totalPagesComputed = Math.max(1, Math.ceil(totalDriversFromApi / limitToUse));
      const currentPageToUse = pag ? (pag.currentPage ?? pag.page ?? page) : page;

      if (pag) {
        setPagination({
          currentPage: currentPageToUse,
          totalPages: totalPagesComputed,
          totalDrivers: totalDriversFromApi,
          hasNext: currentPageToUse < totalPagesComputed,
          hasPrev: currentPageToUse > 1,
          limit: limitToUse,
        });
      } else {
        setPagination((prev) => ({
          ...prev,
          currentPage: currentPageToUse,
          totalDrivers: totalDriversFromApi,
          totalPages: totalPagesComputed,
          hasNext: false,
          hasPrev: false,
          limit: limitToUse,
        }));
      }

      void fetchDriverBreakdown({
        params,
        effectiveFilters,
        listFilteredTotal: totalDriversFromApi,
      });
    } catch (error) {
      console.error("Error fetching driver data:", error);
      const apiMessage = error.response?.data?.message;
      const isTimeout =
        error.code === "ECONNABORTED" ||
        error.response?.status === 504 ||
        /timed out|timeout/i.test(String(apiMessage || error.message || ""));
      toast.error(
        isTimeout
          ? "Driver list is taking too long. Try narrowing filters (status, dates, or document type)."
          : apiMessage || "Failed to fetch drivers"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch drivers when status filter from URL changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchDrivers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Reset panel-card driven filters whenever the user picks a different assigned employee.
  // Prevents a stale panel highlight (e.g. "Pending") from one employee leaking into another's stats view.
  useEffect(() => {
    setFilters((f) => {
      if (!f.callTypeAny && !f.initialCallStatus && !f.panelStatus && !f.hasCompletedRide && !f.locationNotAvailable) return f;
      return {
        ...f,
        callTypeAny: "",
        initialCallStatus: "",
        panelStatus: "",
        hasCompletedRide: "",
        locationNotAvailable: "",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.assignedEmployeeId]);

  // Load new employees for Assigned Employee filter dropdown
  useEffect(() => {
    axiosInstance
      .get(`${API.ADMIN.EMPLOYEES_LIST}?employeeType=new&limit=100`)
      .then((res) => {
        const list = res.data?.employees || res.data?.data || [];
        setAssignedEmployeeFilterOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // Close Contact Source dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contactSourceDropdownRef.current && !contactSourceDropdownRef.current.contains(e.target)) {
        setContactSourceDropdownOpen(false);
      }
    };
    if (contactSourceDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [contactSourceDropdownOpen]);

  // Close State & UT dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (stateUtDropdownRef.current && !stateUtDropdownRef.current.contains(e.target)) {
        setStateUtDropdownOpen(false);
      }
    };
    if (stateUtDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [stateUtDropdownOpen]);

  const mainStartYmd = toLocalDateString(startDate) || "";
  const mainEndYmd = toLocalDateString(endDate) || "";
  const hasMainDateRange = Boolean(mainStartYmd && mainEndYmd);

  useEffect(() => {
    if (!compareEnabled || compareMode === "none") return;
    const preview = computeComparisonRange(
      compareMode,
      mainStartYmd,
      mainEndYmd,
      compareStartDate,
      compareEndDate
    );
    if (preview && compareMode !== "custom") {
      setCompareStartDate(preview.startDate);
      setCompareEndDate(preview.endDate);
    }
  }, [compareEnabled, compareMode, mainStartYmd, mainEndYmd]);

  useEffect(() => {
    if (!appliedComparison) {
      setDriverComparisonBreakdown(null);
      return;
    }
    void fetchComparisonBreakdown(appliedComparison);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedComparison]);

  useEffect(() => {
    if (!hasDriverFullAccess && hideAssignedToEmployee) {
      setHideAssignedToEmployee(false);
      listParamsRef.current = {
        ...listParamsRef.current,
        hideAssignedToEmployee: false,
      };
    }
  }, [hasDriverFullAccess, hideAssignedToEmployee]);

  const handleCompareToggle = (enabled) => {
    setCompareEnabled(enabled);
    if (!enabled) {
      setAppliedComparison(null);
      setDriverComparisonBreakdown(null);
      setCompareMode("previous_period");
    }
  };

  const handleHideAssignedToggle = (enabled) => {
    if (!hasDriverFullAccess) return;
    setHideAssignedToEmployee(enabled);
    if (enabled) {
      setFilters((f) => ({ ...f, assignedEmployeeId: "" }));
    }
    listParamsRef.current = {
      ...listParamsRef.current,
      hideAssignedToEmployee: enabled,
      filters: enabled
        ? { ...listParamsRef.current.filters, assignedEmployeeId: "" }
        : listParamsRef.current.filters,
    };
    fetchDrivers(1);
  };

  const handleApplyComparison = () => {
    if (!compareEnabled) return;
    const range = computeComparisonRange(
      compareMode,
      mainStartYmd,
      mainEndYmd,
      compareStartDate,
      compareEndDate
    );
    if (!range) return;
    setAppliedComparison(range);
  };

  const handleClearComparison = () => {
    setAppliedComparison(null);
    setDriverComparisonBreakdown(null);
    setCompareMode("previous_period");
  };

  const canApplyComparison =
    dateFilterApplied &&
    compareEnabled &&
    compareMode !== "none" &&
    (compareMode === "custom"
      ? Boolean(compareStartDate && compareEndDate)
      : hasMainDateRange);

  const handleStartDateChange = (value) => {
    setStartDate(value ? fromLocalDateString(value) : null);
    setDateFilterApplied(false);
    setAppliedComparison(null);
    setDriverComparisonBreakdown(null);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value ? fromLocalDateString(value) : null);
    setDateFilterApplied(false);
    setAppliedComparison(null);
    setDriverComparisonBreakdown(null);
  };

  const mainDateRangeLabel = hasMainDateRange
    ? formatBreakdownDateRangeLabel(mainStartYmd, mainEndYmd)
    : "";
  const appliedComparisonLabel = appliedComparison
    ? formatBreakdownDateRangeLabel(
        appliedComparison.startDate,
        appliedComparison.endDate
      )
    : "";

  // Handle search (phone or driver name)
  const handlePhoneSearch = () => {
    if (phoneSearch.trim().length === 0) {
      toast.error("Please enter a phone number or driver name to search");
      return;
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchDrivers(1);
  };

  // Handle clear phone search
  const handleClearPhoneSearch = () => {
    setPhoneSearch("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchDrivers(1);
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const options = {
      timeZone: "Asia/Kolkata", // adjust to your preferred timezone
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    const formatted = new Intl.DateTimeFormat("en-GB", options).format(date);
    const [datePart, timePart] = formatted.split(", ");
    return `${datePart.replace(/\//g, "-")} ${timePart}`;
  };

  const handleFilter = () => {
    // Date filter is optional - if both set, refetch with dates; otherwise refetch with current filters.
    // Apply Filter is a deliberate user action via the dropdowns, so clear any panel-card driven
    // filters (callTypeAny / initialCallStatus / panelStatus) so the dropdowns and the panel never
    // compete; the panel will reset its highlighted card via this state update.
    const clearedPanelFilters = {
      ...filters,
      status:
        filters.status ||
        normalizeDriverStatusKey(filters.panelStatus) ||
        "",
      callTypeAny: "",
      initialCallStatus: "",
      panelStatus: "",
      hasCompletedRide: "",
      locationNotAvailable: "",
    };
    setFilters(clearedPanelFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (startDate && endDate) {
      setDateFilterApplied(true);
    } else {
      setDateFilterApplied(false);
      setAppliedComparison(null);
      setDriverComparisonBreakdown(null);
    }
    fetchDrivers(1, null, clearedPanelFilters);
  };

  // Apply a panel-card driven filter update and refetch immediately, bypassing the
  // listParamsRef stale-state issue by passing the next filters through as overrides.
  const applyPanelFilter = (updates) => {
    const nextFilters = { ...filters, ...updates };
    setFilters(nextFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchDrivers(1, null, nextFilters);
  };

  const handleBreakdownCardClick = (statusKey) => {
    if (statusKey === "Registered") {
      const clearedFilters = {
        ...filters,
        panelStatus: "",
        hasCompletedRide: "",
        locationNotAvailable: "",
        callTypeAny: "",
        initialCallStatus: "",
      };
      if (statusFilter) {
        setFilters(clearedFilters);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        navigate("/drivers");
        return;
      }
      applyPanelFilter({
        panelStatus: "",
        hasCompletedRide: "",
        locationNotAvailable: "",
        callTypeAny: "",
        initialCallStatus: "",
      });
      return;
    }

    if (statusKey === "RideCompleted") {
      const toggled = filters.hasCompletedRide ? "" : "1";
      applyPanelFilter({
        hasCompletedRide: toggled,
        panelStatus: "",
        locationNotAvailable: "",
        callTypeAny: "",
        initialCallStatus: "",
      });
      return;
    }

    if (statusKey === "LocationNotAvailable") {
      const toggled = filters.locationNotAvailable ? "" : "1";
      applyPanelFilter({
        locationNotAvailable: toggled,
        panelStatus: "",
        hasCompletedRide: "",
        callTypeAny: "",
        initialCallStatus: "",
      });
      return;
    }

    const toggledStatus = filters.panelStatus === statusKey ? "" : statusKey;
    applyPanelFilter({
      panelStatus: toggledStatus,
      hasCompletedRide: "",
      locationNotAvailable: "",
      callTypeAny: "",
      initialCallStatus: "",
    });
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    listParamsRef.current = { ...listParamsRef.current, sortConfig: next };
    fetchDrivers(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      listParamsRef.current = { ...listParamsRef.current, sortConfig: next };
      fetchDrivers(1);
      return next;
    });
  };

  const driverSortTh = (columnKey, label, className) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={className}
    />
  );

  const displayDrivers = filteredDrivers;

  // List actions (View/Edit/Delete): SuperAdmin sees all; new-type employees use their explicit driverModule.actions toggles.
  const canShowViewInList = () => {
    if (isSuperAdmin) return true;
    return !!(driverModule?.actions?.view);
  };

  const canShowEditInList = (driver) => {
    if (isSuperAdmin) return true;
    if (driverModule && driverModule.actions?.edit) {
      if (!driverModule.allowedStatuses?.length) return false;
      return driverModule.allowedStatuses.includes(driver?.status);
    }
    return false;
  };

  const canShowDeleteInList = (driver) => {
    if (isSuperAdmin) return true;
    if (driverModule && driverModule.actions?.delete) {
      if (!driverModule.allowedStatuses?.length) return false;
      return driverModule.allowedStatuses.includes(driver?.status);
    }
    return false;
  };

  const handleDelete = async (driverId) => {
    try {
      await axiosInstance.delete(`${API.ADMIN.DRIVER_DELETE}/${driverId}`);
      toast.success("Driver deleted successfully");
      fetchDrivers(pagination.currentPage);
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error(error.response?.data?.message || "Failed to delete driver");
    }
  };

  const refreshDriversList = () => {
    console.log("refreshDriversList called - starting refresh");
    fetchDrivers(pagination.currentPage);
  };

  const handlePageChange = (page) => {
    const clampedPage = Math.max(1, Math.min(page, pagination.totalPages));
    fetchDrivers(clampedPage);
  };

  const handleLimitChange = (newLimit) => {
    const limit = parseInt(newLimit);
    setPagination((prev) => ({ ...prev, limit, currentPage: 1 }));
    fetchDrivers(1, limit);
  };

  const handleBulkUploadSuccess = () => {
    console.log(
      "handleBulkUploadSuccess called - closing modal and refreshing drivers"
    );
    setShowBulkUploadModal(false);

    // Add a small delay to ensure modal state is updated
    setTimeout(() => {
      console.log("Starting delayed refresh of drivers list");
      refreshDriversList();
    }, 200); // Increased delay slightly
  };

  const csvHeaders = [
    { label: "Date", key: "createdAt" },
    { label: "Driver Id", key: "code" },
    { label: "Phone", key: "phone" },
    { label: "Name", key: "fullName" },
    { label: "Wallet Balance", key: "walletBalance" },
    { label: "Created At", key: "createdAt" },
    { label: "Pincode", key: "pincode" },
  ];

  const formatDateForCSV = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const csvData = filteredDrivers.map((driver) => ({
    createdAt: formatDateForCSV(driver.createdAt),
    code: driver.code || "N/A",
    phone: driver.phone || "N/A",
    fullName: driver.fullName || "N/A",
    walletBalance: driver.walletBalance || "0",
    pincode: driver.address?.pincode || "N/A",
  }));

  const handleAddDriver = () => {
    navigate("/drivers/add");
  };

  // Show loading state for the entire page
  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUserTie className="text-6xl text-blue-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading Driver Data
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch driver information...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isNewType && !assignModule?.driver?.enabled) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">You do not have access to the Drivers module.</p>
        </div>
      </div>
    );
  }

  return ( 
    <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Driver Management</h2>
        {(hasDriverFullAccess || canShowQuickUpload || canShowAddDriver) && (
        <div className="flex flex-col sm:flex-row gap-2">
          {canShowQuickUpload && (
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-md flex items-center"
            >
              <FaUpload className="mr-2" />
              Bulk Upload
            </button>
          )}
          {hasDriverFullAccess && (
            <button
              onClick={refreshDriversList}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg shadow-md flex items-center"
              title="Refresh drivers list"
            >
              <FaSpinner className="mr-2" />
              Refresh
            </button>
          )}
          {canShowQuickUpload && (
            <button
              onClick={() => setShowSimpleBulkUploadModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-md flex items-center"
            >
              <FaUpload className="mr-2" />
              Quick Upload
            </button>
          )}
          {canShowAddDriver && (
            <button
              onClick={handleAddDriver}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md flex items-center"
            >
              <FaPlus className="mr-2" />
              Add Driver
            </button>
          )}
          {/* <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`drivers_${new Date().toLocaleDateString()}.csv`}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg shadow-md flex items-center"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </CSVLink> */}
        </div>
        )}
      </div>

      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg mb-6 p-6 border border-blue-100">
        <div className="space-y-4">
          {/* Phone Search Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FaSearch className="mr-2 text-blue-500" />
                Search by phone or driver name
              </label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handlePhoneSearch();
                    }
                  }}
                  placeholder="Enter phone number or driver full name"
                  maxLength={50}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                />
                {phoneSearch && (
                  <button
                    onClick={handleClearPhoneSearch}
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                    title="Clear search"
                  >
                    <FaTimesCircle />
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={handlePhoneSearch}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center font-medium"
            >
              <FaSearch className="mr-2" />
              Search
            </button>
          </div>

          {/* Date Filter Section */}
          <DriverAnalyticsDateFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">DL Issue Date</label>
              <input
                type="date"
                value={toLocalDateString(dlIssueDate) || ""}
                onChange={(e) => setDlIssueDate(fromLocalDateString(e.target.value))}
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">DL Expiry Date</label>
              <input
                type="date"
                value={toLocalDateString(dlExpiryDate) || ""}
                onChange={(e) => setDlExpiryDate(fromLocalDateString(e.target.value))}
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
              />
            </div>

            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">DL Number</label>
              <input
                type="text"
                value={filters.dlNumber}
                onChange={(e) => setFilters((f) => ({ ...f, dlNumber: e.target.value }))}
                placeholder="Type DL number"
                maxLength={20}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Aadhaar Number</label>
              <input
                type="text"
                value={filters.aadhaarNumber}
                onChange={(e) => setFilters((f) => ({ ...f, aadhaarNumber: e.target.value }))}
                placeholder="Type Aadhaar number"
                maxLength={12}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">PAN Number</label>
              <input
                type="text"
                value={filters.panNumber}
                onChange={(e) => setFilters((f) => ({ ...f, panNumber: e.target.value }))}
                placeholder="Type PAN number"
                maxLength={10}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* New Filters: Contact Source (searchable dropdown), Location, Area, Status, Assignee, Reporter, Language, Call Status */}
            <div className="min-w-0 relative" ref={contactSourceDropdownRef}>
              <label className="text-sm font-medium text-gray-700 mb-2">Contact Source</label>
              <button
                type="button"
                onClick={() => {
                  setContactSourceDropdownOpen((o) => !o);
                  if (!contactSourceDropdownOpen) setContactSourceSearch("");
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm text-left flex items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <span className={filters.contactSource ? "text-gray-900" : "text-gray-500"}>
                  {filters.contactSource || "Search not listed"}
                </span>
                <FaSearch className="text-gray-400 ml-2 shrink-0" />
              </button>
              {contactSourceDropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={contactSourceSearch}
                      onChange={(e) => setContactSourceSearch(e.target.value)}
                      placeholder="Search contact source..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                  <ul className="max-h-48 overflow-y-auto py-1">
                    {CONTACT_SOURCE_OPTIONS.filter(
                      (opt) =>
                        !contactSourceSearch.trim() ||
                        opt.label.toLowerCase().includes(contactSourceSearch.trim().toLowerCase())
                    ).map((opt) => (
                      <li key={opt.value || "__empty__"}>
                        <button
                          type="button"
                          onClick={() => {
                            setFilters((f) => ({ ...f, contactSource: opt.value }));
                            setContactSourceDropdownOpen(false);
                            setContactSourceSearch("");
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 ${filters.contactSource === opt.value ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-800"
                            }`}
                        >
                          {opt.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="min-w-0 relative" ref={stateUtDropdownRef}>
              <label className="text-sm font-medium text-gray-700 mb-2">State & UT</label>
              <button
                type="button"
                onClick={() => {
                  setStateUtDropdownOpen((o) => !o);
                  if (!stateUtDropdownOpen) setStateUtSearch("");
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm text-left flex items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <span className={filters.stateUt ? "text-gray-900" : "text-gray-500"}>
                  {getStateUtDisplayLabel(filters.stateUt)}
                </span>
                <FaSearch className="text-gray-400 ml-2 shrink-0" />
              </button>
              {stateUtDropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={stateUtSearch}
                      onChange={(e) => setStateUtSearch(e.target.value)}
                      placeholder="Search state or UT..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                  <ul className="max-h-48 overflow-y-auto py-1">
                    {STATE_UT_OPTIONS.filter((opt) => {
                      if (opt.type === "header") {
                        return !stateUtSearch.trim();
                      }
                      return (
                        !stateUtSearch.trim() ||
                        opt.label.toLowerCase().includes(stateUtSearch.trim().toLowerCase())
                      );
                    }).map((opt) =>
                      opt.type === "header" ? (
                        <li key="state-ut-header">
                          <div className="px-4 py-2 text-xs font-semibold bg-yellow-50 text-gray-700 border-y border-yellow-100">
                            {opt.label}
                          </div>
                        </li>
                      ) : (
                        <li key={opt.value || "__state_empty__"}>
                          <button
                            type="button"
                            onClick={() => {
                              setFilters((f) => ({ ...f, stateUt: opt.value }));
                              setStateUtDropdownOpen(false);
                              setStateUtSearch("");
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 ${filters.stateUt === opt.value ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-800"
                              }`}
                          >
                            {opt.label}
                          </button>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Location</label>
              <select
                value={filters.location}
                onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">Search not listed</option>
                <option value="Hyderabad">Hyderabad</option>
                <option value="Bangalore">Bangalore</option>
                <option value="Delhi">Delhi</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Chennai">Chennai</option>
                <option value="Pune">Pune</option>
                <option value="Chandigarh">Chandigarh</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Area</label>
              <input
                type="text"
                value={filters.area}
                onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))}
                placeholder="Type area"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">— Any —</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Assignee</label>
              <input
                type="text"
                value={filters.assignee}
                onChange={(e) => setFilters((f) => ({ ...f, assignee: e.target.value }))}
                placeholder="Type assignee name"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Assigned Employee</label>
              <select
                value={filters.assignedEmployeeId}
                onChange={(e) => setFilters((f) => ({ ...f, assignedEmployeeId: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Any —</option>
                {assignedEmployeeFilterOptions.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.fullName || emp.name || "Unnamed"}
                    {emp.role ? ` (${emp.role})` : ""}
                    {emp.city ? ` – ${emp.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Reporter</label>
              <input
                type="text"
                value={filters.reporter}
                onChange={(e) => setFilters((f) => ({ ...f, reporter: e.target.value }))}
                placeholder="Type reporter name"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={filters.language}
                onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">Search not listed</option>
                <option value="Hindi">Hindi</option>
                <option value="Telugu">Telugu</option>
                <option value="Tamil">Tamil</option>
                <option value="Kannada">Kannada</option>
                <option value="Marathi">Marathi</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Call Status</label>
              <select
                value={filters.callStatus}
                onChange={(e) => setFilters((f) => ({ ...f, callStatus: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Any —</option>
                {CALL_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filters.callType}
                onChange={(e) => setFilters((f) => ({ ...f, callType: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Any —</option>
                {CALL_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">DL Image</label>
              <select
                value={filters.dlImage}
                onChange={(e) => setFilters((f) => ({ ...f, dlImage: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">— Any —</option>
                <option value="Front">Front</option>
                <option value="Back">Back</option>
                <option value="Both">Both</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Aadhaar Image</label>
              <select
                value={filters.aadhaarImage}
                onChange={(e) => setFilters((f) => ({ ...f, aadhaarImage: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">— Any —</option>
                <option value="Front">Front</option>
                <option value="Back">Back</option>
                <option value="Both">Both</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium text-gray-700 mb-2">Pan Image</label>
              <select
                value={filters.panImage}
                onChange={(e) => setFilters((f) => ({ ...f, panImage: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">— Any —</option>
                <option value="No image">No image</option>
                <option value="Pan Image">Pan Image</option>
              </select>
            </div>

            {/* Apply Filter Button */}
            <button
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg flex items-center font-medium self-end"
              onClick={handleFilter}
            >
              <FaFilter className="mr-2" />
              Apply Filter
            </button>
          </div>

          <DriverCompareFilters
            compareEnabled={compareEnabled}
            onCompareToggle={handleCompareToggle}
            compareMode={compareMode}
            onCompareModeChange={setCompareMode}
            compareStartDate={compareStartDate}
            compareEndDate={compareEndDate}
            onCompareStartChange={setCompareStartDate}
            onCompareEndChange={setCompareEndDate}
            onApplyComparison={handleApplyComparison}
            onClearComparison={handleClearComparison}
            canApplyComparison={canApplyComparison}
            comparisonLoading={comparisonBreakdownLoading}
            appliedComparison={appliedComparison}
            hasMainDateRange={hasMainDateRange}
            showApplyComparisonButton={dateFilterApplied}
          />
        </div>
      </div>

      {/* Driver Details Breakdown (based on applied filter fields) */}
      <div className="bg-white p-4 rounded-lg shadow-lg mb-2">
        <div className="mb-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-1">
              Driver Details Breakdown 
            </h3>
            <p className="text-gray-600 text-sm mb-3">
              Counts are based on applied filters (search name/phone, dates, and other filter fields).
              {mainDateRangeLabel && (
                <span className="ml-1">Date range: {mainDateRangeLabel}</span>
              )}
              {appliedComparison && (
                <span className="ml-1">
                  vs {appliedComparison.label} ({appliedComparisonLabel})
                </span>
              )}
            </p>
          </div>
          <div className="text-right sm:text-right">
            <span className="text-sm font-medium text-gray-600">Total drivers: </span>
            <span className="text-lg font-semibold text-gray-800">
              {Number(driverBreakdownData?.Registered || 0).toLocaleString()}
            </span>
          </div>
        </div>
        {(driverBreakdownLoading || comparisonBreakdownLoading) && (
          <div className="mb-1 text-xs text-gray-500">
            Refreshing filter-based counts...
          </div>
        )}
        <DriverStatusSummaryCards
          summaryData={driverBreakdownData}
          comparisonData={driverComparisonBreakdown}
          showComparison={Boolean(appliedComparison)}
          includeRegistered
          compact
          loading={driverBreakdownLoading}
          activeStatus={
            filters.locationNotAvailable
              ? "LocationNotAvailable"
              : filters.hasCompletedRide
              ? "RideCompleted"
              : filters.panelStatus ||
                normalizeDriverStatusKey(filters.status) ||
                (statusFilter ? normalizeDriverStatusKey(statusFilter) : "Registered")
          }
          onCardClick={handleBreakdownCardClick}
          onJoiningFeeClick={(filter) =>
            navigate(filter ? `/joining-fee?filter=${filter}` : "/joining-fee")
          }
          gridClassName="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-2"
        />
      </div>

      {/* Assigned Employee Stats Panel — visible only when an employee is selected */}
      {(() => {
        const selectedAssignedEmployee = assignedEmployeeFilterOptions.find(
          (e) => e._id === filters.assignedEmployeeId
        );
        const selectedAssignedEmployeeName =
          selectedAssignedEmployee?.fullName ||
          selectedAssignedEmployee?.name ||
          "";
        const handlePanelCardClick = (kind, value) => {
          if (kind === "reset") {
            applyPanelFilter({ callTypeAny: "", initialCallStatus: "", panelStatus: "" });
          } else if (kind === "callTypeAny") {
            applyPanelFilter({ callTypeAny: value, initialCallStatus: "", panelStatus: "" });
          } else if (kind === "initialCallStatus") {
            applyPanelFilter({ initialCallStatus: value, callTypeAny: "", panelStatus: "" });
          } else if (kind === "panelStatus") {
            applyPanelFilter({ panelStatus: value, callTypeAny: "", initialCallStatus: "" });
          }
        };
        return (
          <AssignedEmployeeStatsPanel
            assignedEmployeeId={filters.assignedEmployeeId}
            assignedEmployeeName={selectedAssignedEmployeeName}
            activeFilter={{
              callTypeAny: filters.callTypeAny,
              initialCallStatus: filters.initialCallStatus,
              panelStatus: filters.panelStatus,
            }}
            onCardClick={handlePanelCardClick}
          />
        );
      })()}

      {/* Columns visibility toggle and Assign New Employees */}
      <div className="mb-2 flex justify-end items-center gap-2 relative flex-wrap">
        {hasDriverFullAccess && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white">
            <span className="text-gray-700 whitespace-nowrap">Hide assigned</span>
            <button
              type="button"
              role="switch"
              aria-checked={hideAssignedToEmployee}
              aria-label="Hide drivers assigned to employees from the list only"
              onClick={() => handleHideAssignedToggle(!hideAssignedToEmployee)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                hideAssignedToEmployee ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  hideAssignedToEmployee ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        )}
        {hasDriverFullAccess && (
          <button
            type="button"
            disabled={selectedDriverIds.length === 0}
            onClick={() => {
              setShowAssignEmployeeModal(true);
              setAssignModalEmployeeId("");
              setAssignModalAssignedDate(new Date().toISOString().slice(0, 10));
              setNewEmployeesList([]);
              setAssignModalLoading(true);
              axiosInstance
                .get(`${API.ADMIN.EMPLOYEES_LIST}?employeeType=new&limit=100`)
                .then((res) => {
                  if (res.data?.employees) setNewEmployeesList(res.data.employees);
                  else if (Array.isArray(res.data?.data)) setNewEmployeesList(res.data.data);
                })
                .catch(() => toast.error("Failed to load new employees"))
                .finally(() => setAssignModalLoading(false));
            }}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Assign New Employees
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowColumnOptions((v) => !v)}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
        >
          <FaColumns className="mr-2" />
          Columns
        </button>
        {showColumnOptions && (
          <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-white border rounded-lg shadow-lg z-20 min-w-[180px]">
            {["documents", "locationStatus", "currentLocation", "contactSource", "assignee", "assignedEmployee", "assignedBy", "createdBy", "reporter", "language", "callStatus", "dlIssueDate", "dlExpiryDate", "dlLastUpdatedAt", "ridesCompleted", "driverRating", "totalEarnings"].map((col) => (
              <label key={col} className="flex items-center gap-2 py-1 cursor-pointer capitalize">
                <input
                  type="checkbox"
                  checked={visibleColumns[col]}
                  onChange={(e) => setVisibleColumns((v) => ({ ...v, [col]: e.target.checked }))}
                  className="rounded"
                />
                <span>{col === "documents" ? "Documents" : col === "locationStatus" ? "Location & Status" : col === "currentLocation" ? "Current Location" : col === "dlIssueDate" ? "DL Issue Date" : col === "dlExpiryDate" ? "DL Expiry Date" : col === "dlLastUpdatedAt" ? "DL Last Updated Date & Time" : col === "ridesCompleted" ? "Rides Completed" : col === "driverRating" ? "Driver Rating" : col === "totalEarnings" ? "Total Earnings" : col === "assignedEmployee" ? "Assigned Employee" : col === "assignedBy" ? "Assigned By" : col === "createdBy" ? "Driver Created By" : col.replace(/([A-Z])/g, " $1").trim()}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Drivers List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading drivers...</span>
          </div>
        ) : displayDrivers.length === 0 ? (
          <div className="text-center py-12">
            <FaUserTie className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500">
              {hasNoAllowedDriverStatuses
                ? "No allowed driver statuses configured. Select at least one status in employee settings to view drivers."
                : "No drivers found matching your criteria"}
            </p>
          </div>
        ) : (
          <div ref={tableScrollContainerRef} className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
                <tr>
                  {(isSuperAdmin || driverModule?.fullAccess) && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-12">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={displayDrivers.length > 0 && displayDrivers.every((d) => selectedDriverIds.includes(d._id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedDriverIds(displayDrivers.map((d) => d._id));
                            else setSelectedDriverIds([]);
                          }}
                          className="rounded"
                        />
                        <span>Select</span>
                      </label>
                    </th>
                  )}
                  {driverSortTh(
                    "fullName",
                    "Driver Details",
                    "sticky left-0 z-10 bg-gray-50 min-w-[220px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                  )}
                  {driverSortTh(
                    "phone",
                    "Contact Info",
                    "sticky left-[220px] z-10 bg-gray-50 min-w-[160px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                  )}
                  {visibleColumns.documents && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Documents
                    </th>
                  )}
                  {visibleColumns.locationStatus &&
                    driverSortTh(
                      "city",
                      "Location & Status",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.currentLocation &&
                    driverSortTh(
                      "currentLocationAddress",
                      "Current Location",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.contactSource &&
                    driverSortTh(
                      "contactSource",
                      "Contact Source",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.assignee &&
                    driverSortTh(
                      "assignee",
                      "Assignee",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.assignedEmployee &&
                    driverSortTh(
                      "assignedEmployee",
                      "Assigned Employee",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.assignedBy &&
                    driverSortTh(
                      "assignedBy",
                      "Assigned By",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.createdBy &&
                    driverSortTh(
                      "createdBy",
                      "Driver Created By",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.reporter &&
                    driverSortTh(
                      "reporter",
                      "Reporter",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.language &&
                    driverSortTh(
                      "language",
                      "Language",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.callStatus &&
                    driverSortTh(
                      "callStatus",
                      "Call Status",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.dlIssueDate &&
                    driverSortTh(
                      "dlIssueDate",
                      "DL Issue Date",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.dlExpiryDate &&
                    driverSortTh(
                      "dlExpiryDate",
                      "DL Expiry Date",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.ridesCompleted &&
                    driverSortTh(
                      "ridesCompleted",
                      "Rides Completed",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.driverRating &&
                    driverSortTh(
                      "driverRating",
                      "Driver Rating",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {visibleColumns.totalEarnings &&
                    driverSortTh(
                      "totalEarnings",
                      "Total Earnings",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Account Status
                  </th>
                  )}
                  {driverSortTh(
                    "joiningFeeStatus",
                    "Joining Fee Status",
                    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                  )}
                  {visibleColumns.dlLastUpdatedAt &&
                    driverSortTh(
                      "dlLastUpdatedAt",
                      "DL Last Updated Date & Time",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 whitespace-nowrap"
                    )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayDrivers.map((driver) => {
                  const hasAssignedEmployee = !!(driver.assignee || driver.assignedEmployee);
                  const isYellowRow = (isSuperAdmin || driverModule?.fullAccess) && hasAssignedEmployee;
                  return (
                  <tr key={driver._id} className={`group ${isYellowRow ? "bg-yellow-100 hover:bg-yellow-200" : "hover:bg-gray-50"}`}>
                    {(isSuperAdmin || driverModule?.fullAccess) && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedDriverIds.includes(driver._id)}
                          onChange={() => {
                            setSelectedDriverIds((prev) =>
                              prev.includes(driver._id) ? prev.filter((id) => id !== driver._id) : [...prev, driver._id]
                            );
                          }}
                          className="rounded"
                        />
                      </td>
                    )}
                    <td className={`sticky left-0 z-10 min-w-[220px] px-6 py-4 ${isYellowRow ? "bg-yellow-100 group-hover:bg-yellow-200" : "bg-white group-hover:bg-gray-50"}`}>
                      <div>
                        <div className="flex items-center mb-1">
                          <FaUserTie className="text-blue-500 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {driver.fullName}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          ID: {driver.code}
                        </p>
                        <p className="text-xs text-gray-400 flex items-center mt-1">
                          <FaCalendar className="mr-1" />
                          {driver.createdAt}
                        </p>
                      </div>
                    </td>
                    <td className={`sticky left-[220px] z-10 min-w-[160px] px-6 py-4 ${isYellowRow ? "bg-yellow-100 group-hover:bg-yellow-200" : "bg-white group-hover:bg-gray-50"}`}>
                      <div>
                        <div className="flex items-center mb-1">
                          <FaPhone className="text-green-500 mr-2" />
                          <span className="text-sm text-gray-900">
                            {driver.phone}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">Mobile Number</p>
                      </div>
                    </td>
                    {visibleColumns.documents && (
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-xs">
                            <FaIdCard className="text-purple-500 mr-1" />
                            <span className="text-gray-600">
                              DL: {driver.dlNumber || "Not provided"}
                            </span>
                          </div>
                          <div className="flex items-center text-xs">
                            <FaIdCard className="text-orange-500 mr-1" />
                            <span className="text-gray-600">
                              Aadhaar: {driver.aadhaarNumber || "Not provided"}
                            </span>
                          </div>
                          <div className="flex items-center text-xs">
                            <FaIdCard className="text-indigo-500 mr-1" />
                            <span className="text-gray-600">
                              PAN: {driver.panNumber || "Not provided"}
                            </span>
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.locationStatus && (
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center mb-2">
                            <FaMapMarkerAlt className="text-red-500 mr-2" />
                            <span className="text-sm text-gray-900">
                              {driver.drivingChoice?.city || "Not specified"}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            {driver.status === "Approved" && (
                              <div className="flex items-center">
                                <FaCheckCircle className="text-green-500 mr-1" />
                                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                  {driver.status}
                                </span>
                              </div>
                            )}
                            {driver.status === "Rejected" && (
                              <div className="flex items-center">
                                <FaTimesCircle className="text-red-500 mr-1" />
                                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                  {driver.status}
                                </span>
                              </div>
                            )}
                            {(driver.status === "Pending" ||
                              driver.status === "Revisit") && (
                                <div className="flex items-center">
                                  <FaHourglassHalf className="text-yellow-500 mr-1" />
                                  <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full">
                                    {driver.status}
                                  </span>
                                </div>
                              )}
                            {![
                              "Approved",
                              "Rejected",
                              "Pending",
                              "Revisit",
                            ].includes(driver.status) && (
                                <div className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full w-fit">
                                  {driver.status}
                                </div>
                              )}
                            {driver.status === "Onboarded" && driver.onboardedAt && (
                              <p className="text-xs text-gray-400 flex items-center mt-1">
                                <FaCalendar className="mr-1" />
                                
                                {new Date(driver.onboardedAt).toLocaleDateString("en-GB")}{" "}
                                {new Date(driver.onboardedAt).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                            {["Revisit", "Rejected"].includes(driver.status) && driver.statusUpdatedAt && (
                              <p className="text-xs text-gray-400 flex items-center mt-1">
                                <FaCalendar className="mr-1" />
                                {new Date(driver.statusUpdatedAt).toLocaleDateString("en-GB")}{" "}
                                {new Date(driver.statusUpdatedAt).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                            {["Revisit", "Rejected"].includes(driver.status) &&
                              getRejectedOrRevisitReasons(driver).map((reason, idx) => (
                                <p key={idx} className="text-xs text-red-600 mt-1">
                                  {reason.type}: {reason.statusReason || "No reason provided"}
                                </p>
                              ))}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.currentLocation && (
                      <td className="px-6 py-4">
                        <div className="flex items-start max-w-xs">
                          <FaMapMarkerAlt className="text-red-500 mr-2 mt-0.5 shrink-0" />
                          <span
                            className="text-sm text-gray-900 break-words"
                            title={driver.currentLocationAddress || ""}
                          >
                            {driver.currentLocationAddress?.trim() || "Not available"}
                          </span>
                        </div>
                      </td>
                    )}
                    {visibleColumns.contactSource && (
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => setContactCallStatusModalDriverId(driver._id)}
                          className="flex items-center text-left w-full rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                          title="View or edit Contact & Call Status"
                        >
                          {driver.contactSource ? (
                            <>
                              <FaTag className="text-blue-500 mr-2 text-xs shrink-0" />
                              <span className="text-sm font-medium text-gray-900 underline decoration-blue-200 hover:decoration-blue-500">
                                {driver.contactSource}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm text-gray-500 italic">Add contact source</span>
                          )}
                        </button>
                      </td>
                    )}

                    {visibleColumns.assignee && (
                      <td className="px-6 py-4">
                        <div>
                          {driver.customerCareAssignee ? (
                            <span className="text-sm font-medium text-gray-900">{driver.customerCareAssignee}</span>
                          ) : driver.assignee && driver.assignee._id ? (
                            <Link
                              to={`/employees/profile/${driver.assignee._id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                            >
                              {driver.assignee.name || "N/A"}
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                        </div>
                      </td>
                    )}

                    {visibleColumns.assignedEmployee && (
                      <td className="px-6 py-4">
                        <div>
                          {driver.assignee && driver.assignee._id ? (
                            <Link
                              to={`/employees/profile/${driver.assignee._id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                            >
                              {driver.assignee.name || driver.assignedEmployee?.fullName || "N/A"}
                            </Link>
                          ) : driver.assignedEmployee?.fullName ? (
                            <Link
                              to={`/employees/profile/${driver.assignedEmployee._id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                            >
                              {driver.assignedEmployee.fullName}
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                          {(driver.assignee || driver.assignedEmployee) && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {driver.assignedDate
                                ? new Date(driver.assignedDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                : "N/A"}
                            </div>
                          )}
                        </div>
                      </td>
                    )}

                    {visibleColumns.assignedBy && (
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {driver.assignedByName || "N/A"}
                        </span>
                      </td>
                    )}
                    {visibleColumns.createdBy && (
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {driver.createdByName || "N/A"}
                          </span>                         
                        </div>
                      </td>
                    )}

                    {visibleColumns.reporter && (
                      <td className="px-6 py-4">
                        <div>
                          {driver.reporter ? (
                            <span className="text-sm font-medium text-gray-900">
                              {driver.reporter}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                    )}

                    {visibleColumns.language && (
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {driver.language ? (
                            <>
                              <FaGlobe className="text-green-500 mr-2 text-xs" />
                              <span className="text-sm font-medium text-gray-900">
                                {driver.language}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                    )}

                    {visibleColumns.callStatus && (
                      <td className="px-6 py-4">
                        {driver.callStatusHistory && driver.callStatusHistory.length > 0 ? (
                          (() => {
                            const latestEntry = driver.callStatusHistory[driver.callStatusHistory.length - 1];
                            const entryDate = new Date(latestEntry.date);
                            return (
                              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100 min-w-[200px]">
                                <div className="space-y-2">
                                  {/* Date */}
                                  <div className="flex items-center">
                                    <FaCalendar className="text-purple-500 mr-2 text-xs" />
                                    <span className="text-xs font-medium text-gray-700">Date:</span>
                                    <span className="text-xs font-semibold text-gray-900 ml-1">
                                      {entryDate.toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </span>
                                  </div>
                                  {/* Type */}
                                  <div className="flex items-center">
                                    <FaClock className="text-purple-500 mr-2 text-xs" />
                                    <span className="text-xs font-medium text-gray-700">Type:</span>
                                    <span className="text-xs font-semibold text-purple-700 ml-1">
                                      {latestEntry.type}
                                    </span>
                                  </div>
                                  {/* Status */}
                                  <div className="flex items-center">
                                    <FaCheckCircle className="text-purple-500 mr-2 text-xs" />
                                    <span className="text-xs font-medium text-gray-700">Status:</span>
                                    <span className="text-xs font-semibold text-purple-700 ml-1">
                                      {latestEntry.status === "Custom Filed"
                                        ? `Custom Filed - ${latestEntry.customStatusValue?.trim() || latestEntry.notes?.trim() || ""}`.replace(/ - $/, "")
                                        : latestEntry.status}
                                    </span>
                                  </div>
                                  {/* Notes */}
                                  {latestEntry.notes && (
                                    <div className="mt-2 pt-2 border-t border-purple-200">
                                      <p className="text-xs text-gray-600" title={latestEntry.notes}>
                                        {latestEntry.notes.length > 50
                                          ? `${latestEntry.notes.substring(0, 50)}...`
                                          : latestEntry.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                    )}

                    {visibleColumns.dlIssueDate && (
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {driver.dl?.dateOfIssue
                          ? new Date(driver.dl.dateOfIssue).toLocaleDateString("en-GB")
                          : driver.dateOfIssue || "N/A"}
                      </td>
                    )}
                    {visibleColumns.dlExpiryDate && (
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {driver.dl?.dateOfExpire
                          ? new Date(driver.dl.dateOfExpire).toLocaleDateString("en-GB")
                          : driver.dateOfExpire || "N/A"}
                      </td>
                    )}
                    {visibleColumns.ridesCompleted && (
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {driver.totalRides ?? driver.totalRides ?? "N/A"}
                      </td>
                    )}
                    {visibleColumns.driverRating && (
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {driver.overallRating?.value != null && driver.overallRating?.value !== ""
                          ? driver.overallRating?.value
                          : "N/A"}
                      </td>
                    )}
                    {visibleColumns.totalEarnings && (
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {driver.totalPayout != null
                          ? `₹${Number(driver.totalPayout).toLocaleString("en-IN")}`
                          : "N/A"}
                      </td>
                    )}

                    {isSuperAdmin && (
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${driver.isActive !== false ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                            {driver.isActive !== false ? "Active" : "Inactive"}
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await axiosInstance.patch(API.ADMIN.DRIVER_ACTIVE_STATUS(driver._id), { isActive: driver.isActive === false });
                                fetchDrivers(pagination.currentPage);
                              } catch (err) {
                                toast.error(err.response?.data?.message || "Failed to update active status");
                              }
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {driver.isActive !== false ? "Set Inactive" : "Set Active"}
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          String(driver.joiningFeeApplicable || "No").toLowerCase() === "yes"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {driver.joiningFeeApplicable || "No"}
                      </span>
                    </td>
                    {visibleColumns.dlLastUpdatedAt && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {driver.dlLastUpdatedAt ||
                        driver.dl?.updatedAt ||
                        driver.dl?.createdAt ? (
                          <span className="text-xs text-gray-400 inline-flex items-center">
                            <FaCalendar className="mr-1 shrink-0" />
                            {formatDateTime(
                              driver.dlLastUpdatedAt ||
                                driver.dl?.updatedAt ||
                                driver.dl?.createdAt
                            )}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-700">N/A</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {canShowViewInList() && (
                          <Link
                            to={`/drivers/detail/${driver._id}`}
                            className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-150"
                            title="View Details"
                          >
                            <FaEye />
                          </Link>
                        )}
                        {driver.hasActivityLogs && (
                          <button
                            type="button"
                            onClick={() =>
                              setActivityLogsModal({
                                show: true,
                                module: "Driver",
                                entityId: driver._id,
                              })
                            }
                            className="text-indigo-600 hover:text-indigo-800 p-2 rounded-lg hover:bg-indigo-50 transition-colors duration-150 inline-flex items-center text-sm"
                            title="View Activity Logs"
                          >
                            <FaHistory />
                          </button>
                        )}
                        {/* {canShowEditInList(driver) && (
                          <button
                            type="button"
                            onClick={() => navigate(`/drivers/edit/${driver._id}`)}
                            className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50 transition-colors duration-150"
                            title="Edit Assignee & Reporter"
                          >
                            <FaEdit />
                          </button>
                        )} */}
                        {/* {canShowDeleteInList(driver) && (
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this driver?")) {
                                handleDelete(driver._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors duration-150"
                            title="Delete driver"
                          >
                            <FaTrash />
                          </button>
                        )} */}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalDrivers
              )}{" "}
              of {pagination.totalDrivers} results
            </div>

            <div className="flex items-center gap-4">
              {/* Limit Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers - only 1..totalPages */}
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(pagination.totalPages, 5) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }
                      if (pageNum < 1 || pageNum > pagination.totalPages) return null;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 border rounded text-sm transition-colors ${pageNum === pagination.currentPage
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Upload Modal */}
      <DriverBulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={handleBulkUploadSuccess}
      />

      {/* Simple Bulk Upload Modal */}
      <SimpleDriverBulkUploadModal
        isOpen={showSimpleBulkUploadModal}
        onClose={() => setShowSimpleBulkUploadModal(false)}
        onSuccess={() => {
          setShowSimpleBulkUploadModal(false);
          fetchDrivers(pagination.currentPage);
        }}
      />

      {/* Contact & Call Status modal – opened when clicking driver Contact Source */}
      <DriverContactCallStatusModal
        driverId={contactCallStatusModalDriverId}
        onClose={() => setContactCallStatusModalDriverId(null)}
        onSaved={() => {
          if (tableScrollContainerRef.current) {
            scrollPositionToRestoreRef.current = tableScrollContainerRef.current.scrollTop;
          }
          fetchDrivers(listParamsRef.current.pagination.currentPage);
        }}
      />
      <ActivityLogsModal
        show={activityLogsModal.show}
        module={activityLogsModal.module}
        entityId={activityLogsModal.entityId}
        onClose={() =>
          setActivityLogsModal({ show: false, module: "Driver", entityId: null })
        }
      />

      {/* Assign drivers to employee modal (SuperAdmin) */}
      {showAssignEmployeeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !assignSubmitLoading && setShowAssignEmployeeModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign drivers to employee</h3>
            <p className="text-sm text-gray-600 mb-3">
              {selectedDriverIds.length} driver(s) selected. Choose a new employee to assign.
            </p>
            {assignModalLoading ? (
              <div className="flex items-center gap-2 text-gray-500 py-4">
                <FaSpinner className="animate-spin" /> Loading employees...
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">New employee</label>
                <select
                  value={assignModalEmployeeId}
                  onChange={(e) => setAssignModalEmployeeId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">— Select employee —</option>
                  {newEmployeesList.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.fullName || emp.name || "Unnamed"}
                      {emp.role ? ` (${emp.role})` : ""}
                      {emp.city ? ` – ${emp.city}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned date</label>
              <input
                type="date"
                value={assignModalAssignedDate}
                onChange={(e) => setAssignModalAssignedDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={assignSubmitLoading}
                onClick={() => !assignSubmitLoading && setShowAssignEmployeeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={assignSubmitLoading || assignModalLoading || !assignModalEmployeeId}
                onClick={async () => {
                  if (!assignModalEmployeeId || selectedDriverIds.length === 0) return;
                  setAssignSubmitLoading(true);
                  try {
                    const res = await axiosInstance.post(API.ADMIN.DRIVERS_BULK_ASSIGN, {
                      driverIds: selectedDriverIds,
                      employeeId: assignModalEmployeeId,
                      assignedDate: assignModalAssignedDate,
                    });
                    if (res.data?.success) {
                      toast.success(res.data.message || "Drivers assigned successfully");
                      setShowAssignEmployeeModal(false);
                      setSelectedDriverIds([]);
                      setAssignModalEmployeeId("");
                      setAssignModalAssignedDate(new Date().toISOString().slice(0, 10));
                      fetchDrivers(pagination.currentPage);
                    } else {
                      toast.error(res.data?.message || "Failed to assign drivers");
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to assign drivers");
                  } finally {
                    setAssignSubmitLoading(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {assignSubmitLoading ? <FaSpinner className="animate-spin" /> : null}
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
